/**
 * Import required libraries
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Router } from "react-router";
import { createBrowserHistory } from "history";
import { makeStyles, useTheme } from "@material-ui/core";
import { connect, useSelector } from "react-redux";
import { useIcon, useLabel } from "Utils";
import Backdrop from "@material-ui/core/Backdrop"
import Box from "@material-ui/core/Box"
import Typography from "@material-ui/core/Typography"
import CircularProgress from "@material-ui/core/CircularProgress"
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import WifiOffIcon from '@material-ui/icons/WifiOff';

/**
 * Import required elements
 */
import AppBar from "../AppBar";
import { useSocketInterrupted } from "../../utils/index.js";

const history = createBrowserHistory();

const labels = {
  reconnecting: {
    default: "Reconnecting",
    fr: "Reconnexion",
  },
  connected: {
    default: "Connected",
    fr: "Connecté",
  },
  disconnectedSince: {
    default: "Disconnected since",
    fr: "Déconnecté depuis",
  }
}

const DisconnectedAlert = React.memo(({ disconnected }) => {
  const disconnectedAt = useSelector(s => s.simtopy.socketDisconnectedAt)

  const getTime = useCallback(() => {
    const duration = Date.now() - disconnectedAt
    const s = (duration - (duration % 1000)) % 60_000
    const min = duration - (duration % 60_000)
    return `${min / 60_000} min ${s / 1000} s`
  }, [disconnectedAt])

  const [time, setTime] = useState(getTime)
  const intervalRef = useRef(null)

  useEffect(() => {
    let mounted = true
    clearInterval(intervalRef.current)
    let interval = intervalRef.current = setInterval(() => {
      if (mounted) setTime(getTime)
    }, 1000);
    return () => {
      clearInterval(interval)
      mounted = false
    }
  }, [getTime])

  const label = useLabel(disconnected ? labels.disconnectedSince : labels.connected)

  return (
    <Alert
      severity={disconnected ? "error" : "success"}
      elevation={6}
      variant="filled"
      icon={disconnected ? <WifiOffIcon /> : undefined}
    >
      {label} {disconnected ? time : null}
    </Alert>
  )
})

const transitionDuration = {
  exit: 10_000
}

const DisconnectedFeedback = React.memo(() => {
  const zIndex = useTheme().zIndex.snackbar
  const disconnected = useSocketInterrupted()
  const [count, setCount] = useState(0)
  useEffect(() => {
    if (disconnected) setCount(n => n + 1)
  }, [disconnected])
  return (
    <React.Fragment>
      <Backdrop
        open={disconnected}
        style={useMemo(() => ({
          zIndex: zIndex + 1,
          color: '#fff',
        }), [zIndex])}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          gridColumnGap="15px"
        >
          <Typography>{useLabel(labels.reconnecting)}...</Typography>
          <CircularProgress color="inherit" />
        </Box>
      </Backdrop>
      <Snackbar
        key={`socket_disconnected#${count}`}
        open={disconnected}
        transitionDuration={transitionDuration}
        style={useMemo(() => ({
          zIndex: zIndex + 2,
        }), [zIndex])}
      >
        <DisconnectedAlert disconnected={disconnected} />
      </Snackbar>
    </React.Fragment>
  )
})

/**
 * App Component
 */
function App(props) {
  // CSS classnames
  const classes = styles();

  //--------------------------------------favicon-----------------------------------------

  const { faviconFile, headTitle, timestamp, footer = null, children = null } = props;
  const faviconFileUid = faviconFile ? faviconFile.uid : null
  const favicon = useIcon(faviconFileUid, timestamp);

  useEffect(() => {
    //Modification du logo de l'onglet
    if (favicon) {
      //console.log("modification du favicon : nouveau favicon : ", faviconFileUid)

      var link = document.querySelector("link[rel*='icon']")// || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'icon';
      link.href = favicon;
      //document.getElementsByTagName('head')[0].appendChild(link);
      // var link = document.querySelector("link[rel*='shortcut icon']")// || document.createElement('link');
      // link.type = 'image/x-icon';
      // link.rel = 'shortcut icon';
      // link.href = favicon;
      // document.getElementsByTagName('head')[0].appendChild(link);
    }
  }, [favicon])

  //---------------------------------------headtitle---------------------------------------------

  useEffect(() => {
    if (headTitle) {
      //console.log("modification du titre de l'onglet ")
      document.title = headTitle
    }
  }, [headTitle])

  return (
    <Router history={history}>
      <div className={classes.App}>
        <AppBar />

        <div className={classes.Content}>{children}</div>

        {footer}
      </div>
      <DisconnectedFeedback />
    </Router>
  );
}

const styles = makeStyles(theme => ({
  "@global html": {
    height: "100vh",
    width: "100vw",
    overflow: "hidden",
    background: theme.palette.background.body,
    margin: 0,
    padding: 0,
  },
  "@global body": {
    margin: 0,
    padding: 0,
    height: "100%",
    width: "100%",
    overflow: "hidden"
  },
  App: {
    display: "flex",
    flexFlow: "column",
    height: "100vh",
    width: "100vw",
    textAlign: "center",
    overflow: "hidden",
  },
  Content: {
    position: "relative",
    display: "flex",
    width: "100%",
    height: "100%",
    overflowY: "auto"
  },
}));

export default connect(state => {

  let { faviconFile } = state.simtopy || {};
  let file = faviconFile ? state.list.file[faviconFile.uid] : null
  let { timestamp } = file || faviconFile || {};
  let currentTheme = state.simtopy.selectedTheme ? state.list.theme[state.simtopy.selectedTheme] : null;
  let headTitle = (currentTheme && currentTheme.headTitle) ? currentTheme.headTitle : state.locale.initTheme.headTitle;
  return {
    timestamp,
    faviconFile,
    headTitle
  };
})(App);
